// 18f — the service's KnowledgeDocument → the register row.
//
// The artboard shows two ingest states where the column could show four. The
// data has four (`pending | processing | ready | failed`), so all four are
// mapped — but the DESIGNED richness of two of them is not invented:
//
//   EXTRACTING has no `done/total` here. `KnowledgeDocument` carries no
//   progress counter, and `EXTRACTING 12/48` assembled from nothing would be
//   the one thing on this screen that actively lies about work in flight.
//   The chip says EXTRACTING and stops.
//
//   FAILED has no reason unless the upload actually recorded one. A failure
//   chip with an invented cause sends someone to fix the wrong thing.
//
// Page count is the honest headline instead: 48 pages beside a 2.4 MB file
// says the assistant has real context; 1 page beside the same size says it has
// a scanned cover sheet. That distinction is already in the payload and was
// being rendered as a footnote.

import type { KnowledgeDocument } from '@services/knowledge.service';
import type { DocumentRow, DocumentState } from './types';

/** Bytes as the row shows them. Never '0 B' for a missing size — that is a fact we lack. */
export function fileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
}

const reasonOf = (doc: KnowledgeDocument): string => {
  const meta = doc.metadata as Record<string, unknown> | undefined;
  for (const key of ['error', 'failureReason', 'message']) {
    const value = meta?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return 'No reason was recorded';
};

/**
 * What the ingest pipeline has actually done to this file.
 *
 * An unrecognised status reads as extracting rather than ready: a file the
 * client cannot classify has not been shown to be searchable, and claiming
 * READY is the error that matters here. Being wrong towards "still working"
 * costs a person one refresh; being wrong towards "ready" costs them a search
 * that silently returns nothing.
 */
export function documentState(doc: KnowledgeDocument): DocumentState {
  switch (String(doc.status ?? '').toLowerCase()) {
    case 'ready':
      return { kind: 'ready' };
    case 'failed':
      return { kind: 'failed', reason: reasonOf(doc) };
    default:
      return { kind: 'extracting' };
  }
}

export function toDocumentRow(
  doc: KnowledgeDocument,
  categoryName?: string | null,
): DocumentRow {
  return {
    id: doc.id,
    title: doc.title || doc.filename || 'Untitled document',
    fileName: doc.filename ?? '',
    category: categoryName ?? null,
    state: documentState(doc),
    // 0 pages is not a page count — an extractor that reported nothing and one
    // that reported zero are the same absence, and '—' is the honest render.
    pages: doc.pageCount && doc.pageCount > 0 ? doc.pageCount : null,
    sizeBytes: doc.size ?? 0,
    citedCount: null,
  };
}

/**
 * The one-line summary above the table.
 *
 * The page clause is omitted entirely when nothing reports a page count,
 * rather than claiming `0 PAGES INDEXED` — which would read as "we indexed it
 * and found nothing" instead of "we do not know".
 */
export function documentsMeta(rows: DocumentRow[], total?: number): string {
  const files = total ?? rows.length;
  const pages = rows.reduce((n, r) => n + (r.pages ?? 0), 0);
  const head = `${files} ${files === 1 ? 'file' : 'files'}`;
  return pages > 0 ? `${head} · ${pages.toLocaleString()} pages indexed` : head;
}
